import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, BookOpen, User, LogOut, Sun, Moon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useAuth } from "@/contexts/AuthContext";

const NAV_LINKS = [
  { to: "/gallery", label: "Explore" },
  { to: "/flashcards", label: "Flashcards" },
  { to: "/quiz", label: "Quiz" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [dark, setDark] = useState(() => localStorage.getItem("sprakbild_theme") === "dark");
  const { pathname } = useLocation();
  const { user, logout } = useAuth();

  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("sprakbild_theme", dark ? "dark" : "light");
  }, [dark]);

  const handleLogout = async () => {
    await logout();
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-nordic-bg/80 backdrop-blur-xl border-b border-nordic-wood/20" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2 group" data-testid="navbar-logo">
            <BookOpen className="w-6 h-6 text-nordic-blue" />
            <span className="font-heading text-xl font-semibold text-nordic-text tracking-tight">Bildkort</span>
          </Link>

          <div className="hidden md:flex items-center gap-1">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                data-testid={`nav-${link.label.toLowerCase()}`}
                className={`px-4 py-2 rounded-full text-sm font-medium ${
                  pathname.startsWith(link.to)
                    ? "bg-nordic-blue text-white"
                    : "text-nordic-text-secondary hover:text-nordic-text hover:bg-nordic-wood/20"
                }`}
                style={{ transition: "background-color 0.2s ease, color 0.2s ease" }}
              >
                {link.label}
              </Link>
            ))}

            <button
              onClick={() => setDark((d) => !d)}
              className="ml-2 p-2 rounded-full text-nordic-text-secondary hover:text-nordic-text hover:bg-nordic-wood/20"
              style={{ transition: "background-color 0.2s ease, color 0.2s ease" }}
              aria-label="Toggle dark mode"
              data-testid="theme-toggle"
            >
              {dark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>

            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="ml-3 flex items-center gap-2 px-3 py-1.5 rounded-full hover:bg-nordic-wood/20" style={{ transition: "background-color 0.2s ease" }} data-testid="user-menu-btn">
                    {user.picture ? (
                      <img src={user.picture} alt={user.name} className="w-7 h-7 rounded-full" />
                    ) : (
                      <div className="w-7 h-7 rounded-full bg-nordic-blue/10 flex items-center justify-center">
                        <span className="text-xs font-medium text-nordic-blue">{user.name?.[0]?.toUpperCase()}</span>
                      </div>
                    )}
                    <span className="text-sm font-medium text-nordic-text hidden lg:inline">{user.name}</span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48 rounded-xl">
                  <DropdownMenuItem asChild>
                    <Link to="/profile" className="flex items-center gap-2 cursor-pointer" data-testid="menu-profile">
                      <User className="w-4 h-4" /> Profile
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="flex items-center gap-2 cursor-pointer text-nordic-error" data-testid="menu-logout">
                    <LogOut className="w-4 h-4" /> Log Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link to="/login" data-testid="nav-login">
                <Button variant="outline" className="ml-3 rounded-full border-nordic-blue/30 text-nordic-blue text-sm px-5 hover:bg-nordic-blue/5" style={{ transition: "background-color 0.2s ease" }}>
                  Sign In
                </Button>
              </Link>
            )}
          </div>

          <div className="flex items-center gap-1 md:hidden">
            <button onClick={() => setDark((d) => !d)} className="p-2 rounded-full text-nordic-text-secondary" aria-label="Toggle dark mode" data-testid="theme-toggle-mobile">
              {dark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>
            <Button variant="ghost" size="icon" className="text-nordic-text" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          </div>
        </div>
      </div>

      {open && (
        <div className="md:hidden bg-nordic-bg/95 backdrop-blur-xl border-b border-nordic-wood/20 px-4 pb-4" data-testid="mobile-menu">
          {NAV_LINKS.map((link) => (
            <Link key={link.to} to={link.to} onClick={() => setOpen(false)}
              className={`block px-4 py-3 rounded-xl text-sm font-medium mb-1 ${
                pathname.startsWith(link.to) ? "bg-nordic-blue text-white" : "text-nordic-text-secondary hover:bg-nordic-wood/20"
              }`}
            >
              {link.label}
            </Link>
          ))}
          {user ? (
            <>
              <Link to="/profile" onClick={() => setOpen(false)} className="block px-4 py-3 rounded-xl text-sm font-medium text-nordic-text-secondary hover:bg-nordic-wood/20">
                Profile
              </Link>
              <button onClick={() => { handleLogout(); setOpen(false); }} className="block w-full text-left px-4 py-3 rounded-xl text-sm font-medium text-nordic-error hover:bg-nordic-error/5">
                Log Out
              </button>
            </>
          ) : (
            <Link to="/login" onClick={() => setOpen(false)} className="block px-4 py-3 rounded-xl text-sm font-medium text-nordic-blue hover:bg-nordic-blue/5">
              Sign In
            </Link>
          )}
        </div>
      )}
    </nav>
  );
};
